import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProfileDiv from "../../Components/ProfileDiv/ProfileDiv";
import UserInfo from "../../Components/UserInfo/UserInfo";
import ChangePassword from "../../Components/ChangePassword/ChangePassword";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Badge from "react-bootstrap/Badge";
import { useApiError } from "../../utils/useApiError";
import ValidationErrorModal from "../../Components/ValidationErrorModal/ValidationErrorModal";
import ConfirmModal from "../../Components/ConfirmModal/ConfirmModal";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import PageInfoModal from "../../Components/PageInfoModal/PageInfoModal";
import SearchButton from "../../Components/LoadingButtons/SearchButton";
import DataLoader from "../../Components/Spinner/DataLoader";

const PROFIL_INFO_ITEMS = [
  {
    icon: "person-badge",
    text: "İstifadəçi məlumatları tabında adınızı, email və əlaqə məlumatlarınıza baxa bilərsiniz.",
  },
  {
    icon: "key-fill",
    text: "Şifrəni dəyiş tabında mövcud şifrənizi dəyişdirə bilərsiniz.",
  },
  {
    icon: "wallet2",
    text: "Cüzdan (💳) düyməsinə klik edərək cərimə və bonus məlumatlarınıza baxa bilərsiniz.",
  },
  {
    icon: "calendar3",
    text: 'Cərimə modalında tarix aralığı seçib "Axtar" düyməsi ilə filtirləyə bilərsiniz.',
  },
  {
    icon: "check-circle",
    text: "Cərimə siyahısında hər sətirə klik edərək onu oxunmuş kimi işarələyə bilərsiniz.",
  },
  {
    icon: "hand-thumbs-up",
    text: '"Razıyam" və ya "Razı deyiləm" düymələri ilə cərimə qeydlərinə münasibət bildirə bilərsiniz.',
  },
];

const getLast30DaysRange = () => {
  const endDateObj = new Date();
  const beginDateObj = new Date();
  beginDateObj.setDate(beginDateObj.getDate() - 30);

  const formatDate = (d) => {
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
  };

  return {
    beginDate: formatDate(beginDateObj),
    endDate: formatDate(endDateObj),
  };
};

const ProfilPage = () => {
  const [activeTab, setActiveTab] = useState("userInfo");
  const [showFinanceModal, setShowFinanceModal] = useState(false);
  const [financeTab, setFinanceTab] = useState("penalties");
  const [notificationCount, setNotificationCount] = useState(0);
  const [loadingNotification, setLoadingNotification] = useState(false);
  const [loadingPenalties, setLoadingPenalties] = useState(false);
  const [penalties, setPenalties] = useState([]);
  const [selectedPenaltyId, setSelectedPenaltyId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [teacherReasonInput, setTeacherReasonInput] = useState("");
  const [savingTeacherReason, setSavingTeacherReason] = useState(false);
  const [dateRange, setDateRange] = useState(() => getLast30DaysRange());
  const [agreeConfirm, setAgreeConfirm] = useState(null); // null | true | false
  const { errorModal, clearError, handleApiError } = useApiError();

  const buttonStyle = (isActive) => ({
    backgroundColor: isActive ? "#1D4ED8" : "#2563EB",
    color: "#FFFFFF",
    border: isActive ? "1px solid #1D4ED8" : "1px solid #2563EB",
    fontWeight: 600,
    fontSize: 12,
    padding: "6px 12px",
    borderRadius: 6,
    boxShadow: "none",
    lineHeight: 1.2,
  });

  const selectedPenalty = useMemo(
    () => penalties.find((item) => item.id === selectedPenaltyId) || null,
    [penalties, selectedPenaltyId],
  );

  const fetchNotificationCount = useCallback(async () => {
    try {
      setLoadingNotification(true);
      const response = await apiFetch(
        `${BASE_URL}/teachers/get-notification-count`,
      );
      if (!response.ok) {
        throw new Error("Bildiriş sayı alınmadı");
      }
      const data = await response.json();
      setNotificationCount(Number(data?.count) || 0);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoadingNotification(false);
    }
  }, [handleApiError]);

  const fetchPenalties = useCallback(
    async (range) => {
      try {
        setLoadingPenalties(true);
        const [response] = await Promise.all([
          apiFetch(
            `${BASE_URL}/teachers/get-penalties-date-range`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                beginDate: range.beginDate,
                endDate: range.endDate,
              }),
            },
          ),
          new Promise((r) => setTimeout(r, 600)),
        ]);

        if (!response.ok) {
          throw new Error("Cərimələr alınmadı");
        }

        const data = await response.json();
        setPenalties(Array.isArray(data?.penalties) ? data.penalties : []);
        setSelectedPenaltyId(null);
      } catch (error) {
        handleApiError(error);
      } finally {
        setLoadingPenalties(false);
      }
    },
    [handleApiError],
  );

  const handlePenaltyRead = async (penaltyId, isRead) => {
    setSelectedPenaltyId(penaltyId);
    if (isRead) return;

    try {
      await apiFetch(`${BASE_URL}/teacher-penalties/read-by-teacher`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ penaltyId }),
      });
      setPenalties((prev) =>
        prev.map((item) =>
          item.id === penaltyId ? { ...item, isRead: true } : item,
        ),
      );
      setNotificationCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleAgreementChange = async (agreeValue) => {
    if (!selectedPenalty) return;
    const endpoint = agreeValue
      ? `${BASE_URL}/teacher-penalties/agree-with-fine`
      : `${BASE_URL}/teacher-penalties/disagree-with-fine`;

    try {
      setActionLoading(true);
      const response = await apiFetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ penaltyId: selectedPenalty.id }),
      });

      if (!response.ok) {
        throw new Error("Razılıq statusu yenilənmədi");
      }

      setPenalties((prev) =>
        prev.map((item) =>
          item.id === selectedPenalty.id
            ? { ...item, agree: agreeValue }
            : item,
        ),
      );
    } catch (error) {
      handleApiError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const openTeacherReasonModal = () => {
    if (!selectedPenalty) return;
    setTeacherReasonInput(selectedPenalty.teacherReason || "");
    setReasonModalOpen(true);
  };

  const handleSaveTeacherReason = async () => {
    if (!selectedPenalty) return;

    try {
      setSavingTeacherReason(true);
      const response = await apiFetch(
        `${BASE_URL}/teacher-penalties/add-penalty-reason`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: selectedPenalty.id,
            teacherReason: teacherReasonInput,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Müəllim səbəbi yadda saxlanmadı");
      }

      setPenalties((prev) =>
        prev.map((item) =>
          item.id === selectedPenalty.id
            ? { ...item, teacherReason: teacherReasonInput }
            : item,
        ),
      );
      setReasonModalOpen(false);
    } catch (error) {
      handleApiError(error);
    } finally {
      setSavingTeacherReason(false);
    }
  };

  useEffect(() => {
    fetchNotificationCount();
  }, [fetchNotificationCount]);

  useEffect(() => {
    if (!showFinanceModal || financeTab !== "penalties") return;
    fetchPenalties(getLast30DaysRange());
  }, [showFinanceModal, financeTab, fetchPenalties]);

  const notificationButtonStyle = {
    width: 32,
    height: 32,
    borderRadius: 6,
    border: "1px solid #B91C1C",
    background: "#B91C1C",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    cursor: "pointer",
    transition: "background 150ms ease, border-color 150ms ease",
    position: "relative",
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("az-AZ");
  };

  return (
    /* flex-column mobil üçün (alt-alta), flex-md-row planşet/desktop üçün (yan-yana) */
    <>
      <style>{`
        .profile-layout {
          align-items: flex-start;
        }
        .profile-sidebar {
          width: 100%;
          max-width: 320px;
          flex: 0 0 320px;
        }
        .profile-operations {
          min-width: 0;
          width: 100%;
        }
        .profile-content-card {
          width: 100%;
          max-width: 560px;
        }
        @media (max-width: 1199px) {
          .profile-sidebar {
            max-width: 100%;
            flex: 1 1 auto;
          }
          .profile-content-card {
            max-width: 100%;
          }
        }
      `}</style>
      <div className="profile-layout d-flex flex-column flex-xl-row gap-4"
      style={{ padding: "0 4px" }}
      >
      {/* LEFT */}
      <div className="profile-sidebar">
        <ProfileDiv />
      </div>

      {/* RIGHT */}
      <div className="profile-operations flex-grow-1">
        <header className="d-flex flex-wrap gap-3 mb-4 align-items-center">
          <PageInfoModal title="Profil" items={PROFIL_INFO_ITEMS} />
          <Button
            style={buttonStyle(activeTab === "userInfo")}
            onClick={() => setActiveTab("userInfo")}
          >
            İstifadəçi məlumatları
          </Button>
          <Button
            style={buttonStyle(activeTab === "changePassword")}
            onClick={() => setActiveTab("changePassword")}
          >
            Şifrəni dəyiş
          </Button>
          <button
            onClick={() => {
              setActiveTab("maliyye");
              setShowFinanceModal(true);
            }}
            title="Cərimə və bonuslarım"
            style={notificationButtonStyle}
          >
            <i className="bi bi-wallet2"></i>
            {loadingNotification ? (
              <Spinner
                animation="border"
                size="sm"
                style={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  width: 14,
                  height: 14,
                }}
              />
            ) : notificationCount > 0 ? (
              <Badge
                bg="danger"
                pill
                style={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  minWidth: 18,
                  fontSize: 10,
                }}
              >
                {notificationCount > 99 ? "99+" : notificationCount}
              </Badge>
            ) : null}
          </button>
        </header>

        <div
          className="profile-content-card"
          style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: 20 }}
        >
          {activeTab === "userInfo" && <UserInfo />}
          {activeTab === "changePassword" && (
            <ChangePassword onApiError={handleApiError} />
          )}
          {activeTab === "maliyye" && (
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                color: "#9ca3af",
              }}
            >
              <i
                className="bi bi-wallet2"
                style={{
                  fontSize: 40,
                  color: "#fcd34d",
                  display: "block",
                  marginBottom: 12,
                }}
              ></i>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}>
                Cərimə və bonuslarım
              </div>
              <div style={{ fontSize: 12, marginTop: 6 }}>
                Yuxarıdakı cüzdan düyməsi ilə modalı açın
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={showFinanceModal}
        onHide={() => {
          setShowFinanceModal(false);
          fetchNotificationCount();
        }}
        size="xl"
        centered
        contentClassName="border-0 shadow-lg"
        dialogClassName="finance-modal-dialog"
        style={{ "--bs-modal-border-radius": "16px" }}
      >
        <Modal.Header closeButton style={{ padding: "14px 20px" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className="bi bi-wallet2" style={{ color: "#2563EB", fontSize: 16 }}></i>
            Cərimə və bonuslarım
          </Modal.Title>
        </Modal.Header>

        <style>{`.finance-modal-dialog { max-width: 90vw !important; }`}</style>
        <Modal.Body
          style={{
            padding: "16px",
            background: "#F8FAFC",
            height: "78vh",
            overflowY: "auto",
          }}
        >
          {/* Tab keçiricisi */}
          <div style={{ display: "inline-flex", gap: 2, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: 3, marginBottom: 14 }}>
            {[
              { key: "penalties", icon: "exclamation-circle-fill", label: "Cərimələrim" },
              { key: "bonuses",   icon: "star-fill",                label: "Bonuslarım"  },
            ].map((tab) => {
              const isActive = financeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setFinanceTab(tab.key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "6px 14px", borderRadius: 6, border: "none",
                    background: isActive ? "#EFF6FF" : "transparent",
                    color: isActive ? "#2563EB" : "#64748B",
                    fontWeight: isActive ? 600 : 500, fontSize: 13, cursor: "pointer",
                    transition: "background 150ms ease, color 150ms ease",
                  }}
                >
                  <i className={`bi bi-${tab.icon}`} style={{ fontSize: 12 }}></i>
                  {tab.label}
                </button>
              );
            })}
          </div>

          {financeTab === "penalties" && (
            <>
              {/* Filter panel */}
              <div
                style={{
                  display: "flex", alignItems: "flex-end", gap: 10,
                  flexWrap: "wrap", background: "#fff",
                  borderRadius: 8, border: "1px solid #E5E7EB",
                  padding: "12px 14px", marginBottom: 12,
                }}
              >
                {[
                  { label: "BAŞLANĞIC", key: "beginDate" },
                  { label: "SON TARİX", key: "endDate" },
                ].map(({ label, key }) => (
                  <div
                    key={key}
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {label}
                    </span>
                    <input
                      type="date"
                      value={dateRange[key]}
                      onChange={(e) =>
                        setDateRange((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      style={{
                        padding: "6px 10px",
                        borderRadius: 7,
                        border: "1px solid #e2e4e8",
                        background: "#f9fafb",
                        color: "#374151",
                        fontSize: 13,
                        outline: "none",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                ))}
                <SearchButton
                  onClick={() => fetchPenalties(dateRange)}
                  loading={loadingPenalties}
                />

                {/* Razılıq düymələri */}
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <button
                    disabled={!selectedPenalty || actionLoading || savingTeacherReason}
                    onClick={openTeacherReasonModal}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 12px", borderRadius: 6,
                      border: "1px solid", borderColor: !selectedPenalty ? "#E5E7EB" : "#BFDBFE",
                      background: !selectedPenalty ? "#F9FAFB" : "#EFF6FF",
                      color: !selectedPenalty ? "#94A3B8" : "#2563EB",
                      fontWeight: 500, fontSize: 12.5,
                      cursor: !selectedPenalty || actionLoading || savingTeacherReason ? "not-allowed" : "pointer",
                      transition: "all 150ms ease",
                    }}
                  >
                    <i className="bi bi-pencil-square"></i> Səbəb yaz
                  </button>
                  <button
                    disabled={!selectedPenalty || actionLoading}
                    onClick={() => setAgreeConfirm(false)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 12px", borderRadius: 6,
                      border: "1px solid", borderColor: !selectedPenalty ? "#E5E7EB" : "#FECACA",
                      background: !selectedPenalty ? "#F9FAFB" : "#FEF2F2",
                      color: !selectedPenalty ? "#94A3B8" : "#DC2626",
                      fontWeight: 500, fontSize: 12.5,
                      cursor: !selectedPenalty || actionLoading ? "not-allowed" : "pointer",
                      transition: "all 150ms ease",
                    }}
                  >
                    <i className="bi bi-x-circle"></i> Razı deyiləm
                  </button>
                  <button
                    disabled={!selectedPenalty || actionLoading}
                    onClick={() => setAgreeConfirm(true)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 12px", borderRadius: 6,
                      border: "1px solid", borderColor: !selectedPenalty ? "#E5E7EB" : "#BBF7D0",
                      background: !selectedPenalty ? "#F9FAFB" : "#F0FDF4",
                      color: !selectedPenalty ? "#94A3B8" : "#16A34A",
                      fontWeight: 500, fontSize: 12.5,
                      cursor: !selectedPenalty || actionLoading ? "not-allowed" : "pointer",
                      transition: "all 150ms ease",
                    }}
                  >
                    <i className="bi bi-check-circle"></i> Razıyam
                  </button>
                </div>
              </div>

              {/* Cədvəl */}
              <div style={{ position: "relative", minHeight: loadingPenalties ? 120 : 0 }}>
                <DataLoader show={loadingPenalties} />
              </div>
              {!loadingPenalties && penalties.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 0", background: "#fff", borderRadius: 8, border: "1px solid #E5E7EB" }}>
                  <i className="bi bi-inbox" style={{ fontSize: 36, color: "#D1D5DB", display: "block", marginBottom: 10 }}></i>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Cərimə tapılmadı</div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>Seçilmiş tarix aralığında heç bir cərimə qeydi yoxdur.</div>
                </div>
              ) : (
                <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #E5E7EB", overflow: "hidden" }}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E5E7EB" }}>
                          {["Kod","Məbləğ","Tarix","Səbəb","Qrup","Razılıq","Status","Müəllim səbəbi"].map((h) => (
                            <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: 10.5, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {penalties.map((item) => {
                          const isSelected = selectedPenaltyId === item.id;
                          const isUnread = !item.isRead;
                          return (
                            <tr
                              key={item.id}
                              onClick={() => handlePenaltyRead(item.id, item.isRead)}
                              style={{
                                cursor: "pointer",
                                background: isSelected ? "#EFF6FF" : isUnread ? "#FFFBEB" : "#fff",
                                borderBottom: "1px solid #F3F4F6",
                                borderLeft: isSelected ? "2px solid #2563EB" : "2px solid transparent",
                                transition: "background 100ms ease",
                              }}
                              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#F9FAFB"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "#EFF6FF" : isUnread ? "#FFFBEB" : "#fff"; }}
                            >
                              <td style={{ padding: "9px 14px", color: "#94A3B8", fontWeight: 600 }}>#{item.id}</td>
                              <td style={{ padding: "9px 14px" }}>
                                <span style={{ fontWeight: 600, fontSize: 12, color: "#DC2626", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 5, padding: "2px 7px" }}>
                                  {item.amount} ₼
                                </span>
                              </td>
                              <td style={{ padding: "9px 14px", color: "#374151", whiteSpace: "nowrap" }}>
                                <i className="bi bi-calendar3" style={{ color: "#94A3B8", marginRight: 5, fontSize: 11 }}></i>
                                {formatDateTime(item.register)}
                              </td>
                              <td style={{ padding: "9px 14px", color: "#374151", maxWidth: 200 }}>{item.reason || "—"}</td>
                              <td style={{ padding: "9px 14px", whiteSpace: "nowrap" }}>
                                {item.group ? (
                                  <span style={{ fontSize: 11, fontWeight: 600, background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE", borderRadius: 20, padding: "2px 9px" }}>
                                    {item.group}
                                  </span>
                                ) : "—"}
                              </td>
                              <td style={{ padding: "9px 14px" }}>
                                <span style={{
                                  fontSize: 11, fontWeight: 600,
                                  background: item.agree ? "#F0FDF4" : "#FEF2F2",
                                  color: item.agree ? "#16A34A" : "#DC2626",
                                  border: `1px solid ${item.agree ? "#BBF7D0" : "#FECACA"}`,
                                  borderRadius: 20, padding: "2px 9px",
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                }}>
                                  <i className={`bi bi-${item.agree ? "check-circle-fill" : "x-circle-fill"}`} style={{ fontSize: 10 }}></i>
                                  {item.agree ? "Razıyam" : "Razı deyiləm"}
                                </span>
                              </td>
                              <td style={{ padding: "9px 14px" }}>
                                {item.isRead ? (
                                  <span style={{ fontSize: 11, fontWeight: 600, background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0", borderRadius: 20, padding: "2px 9px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    <i className="bi bi-eye-fill" style={{ fontSize: 10 }}></i> Oxunub
                                  </span>
                                ) : (
                                  <span style={{ fontSize: 11, fontWeight: 600, background: "#FFFBEB", color: "#D97706", border: "1px solid #FDE68A", borderRadius: 20, padding: "2px 9px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    <i className="bi bi-circle-fill" style={{ fontSize: 6 }}></i> Yeni
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "9px 14px", color: "#374151", maxWidth: 260 }}>{item.teacherReason || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {financeTab === "bonuses" && (
            <div style={{ textAlign: "center", padding: "56px 0", background: "#fff", borderRadius: 8, border: "1px solid #E5E7EB" }}>
              <div style={{ width: 56, height: 56, borderRadius: 12, background: "#EFF6FF", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "#2563EB", marginBottom: 12 }}>
                <i className="bi bi-stars"></i>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>Bonuslarım</div>
              <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>Bu bölmə hələlik hazırlanır.</div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <ConfirmModal
        show={agreeConfirm !== null}
        onHide={() => setAgreeConfirm(null)}
        onConfirm={async () => {
          await handleAgreementChange(agreeConfirm);
          setAgreeConfirm(null);
        }}
        loading={actionLoading}
        variant={agreeConfirm ? "primary" : "warning"}
        title={
          agreeConfirm ? "Razılığınızı təsdiqləyin" : "Etirazınızı təsdiqləyin"
        }
        message={
          agreeConfirm
            ? `"${selectedPenalty?.reason || "Bu cərimə"}" üzrə razılığınızı bildirmək istədiyinizə əminsiniz?`
            : `"${selectedPenalty?.reason || "Bu cərimə"}" üzrə razı olmadığınızı bildirmək istədiyinizə əminsiniz?`
        }
        confirmText={agreeConfirm ? "Razıyam" : "Razı deyiləm"}
      />

      <Modal
        show={reasonModalOpen}
        onHide={() => setReasonModalOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            Cərimə və bonuslarım
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 8,
              color: "#374151",
            }}
          >
            Səbəbinizi yazın
          </div>
          <textarea
            value={teacherReasonInput}
            onChange={(e) =>
              setTeacherReasonInput(e.target.value.slice(0, 1000))
            }
            maxLength={1000}
            rows={6}
            placeholder="Maksimum 1000 simvol"
            style={{
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "10px 12px",
              resize: "vertical",
              fontSize: 13,
              color: "#111827",
              outline: "none",
            }}
          />
          <div
            style={{
              marginTop: 6,
              textAlign: "right",
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            {teacherReasonInput.length}/1000
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setReasonModalOpen(false)}
            disabled={savingTeacherReason}
          >
            Bağla
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveTeacherReason}
            disabled={
              savingTeacherReason ||
              !selectedPenalty ||
              teacherReasonInput.trim().length === 0
            }
          >
            {savingTeacherReason ? "Yadda saxlanır..." : "Yadda saxla"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ValidationErrorModal
        show={!!errorModal}
        onHide={clearError}
        message={errorModal?.message}
        validations={errorModal?.validations}
      />
      </div>
    </>
  );
};

export default ProfilPage;
